'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef } from 'react';
import type { TeamSlug } from 'src/app/(football)/[team]/_context/types';

interface FullScreenVideoProps {
  isOpen: boolean;
  teamSlug: TeamSlug;
  onVideoEnd: () => void;
}

const CDN_BASE_URL = 'https://cdn.kottonseed.io/assets/football/2026';
const VIDEO_TIMEOUT_MS = 15000; // 15초 타임아웃 (안전장치)

/**
 * 풀스크린 비디오 컴포넌트
 * - Special 등급 + 첫 카드 획득 시 재생
 * - 영상 끝나면 onVideoEnd 콜백 호출
 * - 영상 로드 실패 시 조용히 스킵
 */
export default function FullScreenVideo({ isOpen, teamSlug, onVideoEnd }: FullScreenVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasEndedRef = useRef(false);

  const videoSrc = `${CDN_BASE_URL}/${teamSlug}/main/intro.mp4`;

  // 중복 호출 방지
  const handleEnd = useCallback(() => {
    if (hasEndedRef.current) return;
    hasEndedRef.current = true;
    onVideoEnd();
  }, [onVideoEnd]);

  const handleVideoEnd = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  // 영상 오류 시 조용히 스킵
  const handleVideoError = useCallback(() => {
    console.warn('[FullScreenVideo] 영상 로드 실패, 스킵합니다.');
    handleEnd();
  }, [handleEnd]);

  // isOpen 변경 시 상태 초기화 & 타임아웃 설정
  useEffect(() => {
    if (!isOpen) return;

    hasEndedRef.current = false;

    // 안전장치: 15초 후에도 안 끝나면 강제 종료
    const timeout = setTimeout(() => {
      if (!hasEndedRef.current) {
        console.warn('[FullScreenVideo] 타임아웃, 강제 스킵합니다.');
        handleEnd();
      }
    }, VIDEO_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [isOpen, handleEnd]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-10000 flex items-center justify-center bg-black"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <video
            ref={videoRef}
            src={videoSrc}
            autoPlay
            playsInline
            muted
            onEnded={handleVideoEnd}
            onError={handleVideoError}
            className="h-full w-full object-contain"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
